// Contagem física de inventário — compartilhada pela vistoria de abertura e pelo inventário
// periódico. Tela pensada para o celular: lista agrupada por categoria, campo numérico e botão de
// salvar por linha (Enter salva e pula para o próximo), progresso "32 de 41 contados" e cadastro
// rápido de produto. A contagem é "cega": o saldo do sistema não aparece aqui.
import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, ChevronRight, Plus, RefreshCw, Search } from "lucide-react";
import { getEstoqueCategorias, getEstoqueUnidades, putEstoqueContagem } from "../../api";
import { ModalProduto } from "./CadastrosEstoqueView";
import { NumeroInput, btnStyle, inputStyle, numeroParaInput, parseNumero } from "./ui";

export function agruparPorCategoria(itens) {
  const grupos = new Map();
  for (const item of itens) {
    if (!grupos.has(item.categoria_nome)) grupos.set(item.categoria_nome, []);
    grupos.get(item.categoria_nome).push(item);
  }
  return Array.from(grupos.entries());
}

export function BarraProgresso({ C, feitos, total, rotulo }) {
  const pct = total ? Math.round((feitos / total) * 100) : 0;
  const completo = total > 0 && feitos === total;
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
        <span style={{ fontSize: 15, fontWeight: 800, color: completo ? C.success : C.text }}>{feitos} de {total} {rotulo}</span>
        <span style={{ fontSize: 12, color: C.muted, fontWeight: 600 }}>{pct}%</span>
      </div>
      <div style={{ height: 8, background: C.border, borderRadius: 999, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: completo ? C.success : C.brand, transition: "width 0.3s" }} />
      </div>
    </div>
  );
}

export function Card({ C, children, style }) {
  return <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, ...style }}>{children}</div>;
}


// ─── Contagem ─────────────────────────────────────────────────────────────────

function LinhaContagem({ C, item, inventarioId, podeEditar, onSalvo, registrarInput, focarProximo }) {
  const valorServidor = numeroParaInput(item.quantidade_contada);
  const [texto, setTexto] = useState(valorServidor);
  const [status, setStatus] = useState("idle"); // idle | salvando | salvo | erro
  const [erro, setErro] = useState("");
  const alterado = texto !== valorServidor;

  useEffect(() => { setTexto(valorServidor); }, [valorServidor]);

  async function salvar() {
    if (!alterado) { focarProximo(item.produto_id); return; }
    const numero = parseNumero(texto);
    if (texto.trim() && numero === null) { setErro("Número inválido"); setStatus("erro"); return; }
    setStatus("salvando");
    setErro("");
    try {
      const atualizado = await putEstoqueContagem(inventarioId, item.produto_id, { quantidade_contada: numero });
      onSalvo(atualizado);
      setStatus("salvo");
      focarProximo(item.produto_id);
    } catch (e) {
      setErro(e.message);
      setStatus("erro");
    }
  }

  const contado = item.quantidade_contada !== null && item.quantidade_contada !== undefined;
  const corBotao = status === "erro" ? C.danger : alterado ? C.brand : contado ? C.success : C.border;
  return (
    <div style={{ padding: "10px 12px", borderTop: `1px solid ${C.border}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.25 }}>{item.produto_nome}</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
            {contado ? `Contado${item.contado_por_nome ? ` por ${item.contado_por_nome}` : ""}` : "Não contado"}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          <NumeroInput
            C={C}
            value={texto}
            onChange={(v) => { setTexto(v); setStatus("idle"); setErro(""); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); salvar(); } }}
            disabled={!podeEditar}
            placeholder="Qtd"
            aria-label={`Quantidade de ${item.produto_nome}`}
            style={{ width: 92, fontSize: 16, fontWeight: 700, borderColor: alterado ? C.brand : C.border }}
            onFocus={(e) => e.target.select()}
            enterKeyHint="next"
            {...registrarInput(item.produto_id)}
          />
          <span style={{ fontSize: 12, color: C.muted, width: 38, fontWeight: 600 }}>{item.unidade_sigla}</span>
          {podeEditar && (
            <button
              onClick={salvar}
              disabled={status === "salvando"}
              aria-label={`Salvar contagem de ${item.produto_nome}`}
              style={{ width: 44, height: 44, borderRadius: 10, border: "none", cursor: "pointer", background: corBotao, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", opacity: status === "salvando" ? 0.6 : 1 }}
            >
              {status === "salvando" ? <RefreshCw size={18} className="animate-spin" /> : <Check size={20} />}
            </button>
          )}
        </div>
      </div>
      {erro && <div style={{ fontSize: 12, color: C.danger, marginTop: 4 }}>{erro}</div>}
    </div>
  );
}

export function AbaContagem({ C, inventario: abertura, perm, podeEditar, onItemSalvo, onProdutoCriado }) {
  const [busca, setBusca] = useState("");
  const [soPendentes, setSoPendentes] = useState(false);
  const [recolhidas, setRecolhidas] = useState({});
  const [modalProduto, setModalProduto] = useState(false);
  const [cadastros, setCadastros] = useState({ categorias: [], unidades: [] });
  const inputs = useRef({});
  const { resumo } = abertura;

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return abertura.itens.filter((i) =>
      (!soPendentes || i.quantidade_contada === null) && (!termo || i.produto_nome.toLowerCase().includes(termo))
    );
  }, [abertura.itens, busca, soPendentes]);
  const grupos = agruparPorCategoria(filtrados);
  const ordem = filtrados.map((i) => i.produto_id);

  const registrarInput = (produtoId) => ({ ref: (el) => { inputs.current[produtoId] = el; } });
  const focarProximo = (produtoId) => {
    const idx = ordem.indexOf(produtoId);
    const proximo = ordem.slice(idx + 1).find((id) => inputs.current[id]);
    if (proximo) inputs.current[proximo].focus();
  };

  async function abrirCadastro() {
    if (!cadastros.categorias.length) {
      const [categorias, unidades] = await Promise.all([getEstoqueCategorias(true), getEstoqueUnidades(true)]);
      setCadastros({ categorias, unidades });
    }
    setModalProduto(true);
  }

  return (
    <>
      <div style={{ position: "sticky", top: 58, zIndex: 10, background: C.bg, paddingBottom: 10, marginTop: -4, paddingTop: 4 }}>
        <Card C={C} style={{ padding: 12 }}>
          <BarraProgresso C={C} feitos={resumo.contados} total={resumo.total} rotulo="contados" />
          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <div style={{ position: "relative", flex: "1 1 180px" }}>
              <Search size={15} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: C.muted }} />
              <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar produto" style={{ ...inputStyle(C), paddingLeft: 34 }} />
            </div>
            <button onClick={() => setSoPendentes((v) => !v)} style={{ ...btnStyle(C.brand, !soPendentes), flex: "0 0 auto" }}>
              {soPendentes ? "Mostrando pendentes" : "Só pendentes"}
            </button>
          </div>
        </Card>
      </div>

      {podeEditar && perm.operador && (
        <button onClick={abrirCadastro} style={{ ...btnStyle(C.brand, true), width: "100%", marginBottom: 12 }}>
          <Plus size={16} /> Produto não está na lista? Cadastrar agora
        </button>
      )}

      {grupos.length === 0 && (
        <Card C={C} style={{ textAlign: "center", color: C.muted, fontSize: 13 }}>
          {soPendentes ? "Nenhum produto pendente. 🎉" : "Nenhum produto encontrado."}
        </Card>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {grupos.map(([categoria, itens]) => {
          const doGrupo = abertura.itens.filter((i) => i.categoria_nome === categoria);
          const contadosGrupo = doGrupo.filter((i) => i.quantidade_contada !== null).length;
          const recolhida = recolhidas[categoria];
          return (
            <div key={categoria} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
              <button
                onClick={() => setRecolhidas((r) => ({ ...r, [categoria]: !r[categoria] }))}
                style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "12px", minHeight: 48, background: C.bgActive, border: "none", cursor: "pointer", fontFamily: "inherit", color: C.text }}
              >
                {recolhida ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                <span style={{ fontSize: 14, fontWeight: 800, flex: 1, textAlign: "left" }}>{categoria}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: contadosGrupo === doGrupo.length ? C.success : C.muted }}>
                  {contadosGrupo}/{doGrupo.length}
                </span>
              </button>
              {!recolhida && itens.map((item) => (
                <LinhaContagem
                  key={item.produto_id}
                  C={C}
                  item={item}
                  inventarioId={abertura.id}
                  podeEditar={podeEditar && perm.operador}
                  onSalvo={onItemSalvo}
                  registrarInput={registrarInput}
                  focarProximo={focarProximo}
                />
              ))}
            </div>
          );
        })}
      </div>

      {modalProduto && (
        <ModalProduto
          C={C}
          produto={null}
          categorias={cadastros.categorias}
          unidades={cadastros.unidades}
          podeEditarLimites={false}
          onClose={() => setModalProduto(false)}
          onSalvo={(produto) => { setModalProduto(false); setBusca(produto?.nome || ""); onProdutoCriado(); }}
        />
      )}
    </>
  );
}

